import { DEFAULT_WIDTHS } from './DEFAULT_WIDTHS.js';

// Jedyne miejsce, które składa URL obrazka — po stronie serwera (PictureFile)
// i w przeglądarce (PictureSrcHtml w komponentach Lit). `version` to skrót
// treści pliku źródłowego wyliczony przez gen-picture-const: zmienia się tylko
// razem z plikiem (albo z IMAGE_TRANSFORM_VERSION), więc adres z `?v=` można
// cache'ować jako immutable. Serwer ignoruje query przy wyborze pliku.
// `version` = null daje stały adres bez `?v=` — identyfikator obrazka dla
// Google (sitemap, schema.org, og:image), który nie zmienia się razem z plikiem.
export function buildPictureRoute(
    preRoute: string,
    fileId: string,
    ext: string,
    version: string | null,
    width?: number,
): string {
    const widthPart = width ? `.${width}` : '';
    const versionPart = version === null ? '' : `?v=${version}`;
    return `${preRoute}${fileId}${widthPart}.${ext}${versionPart}`;
}

const SOURCE_FAMILY = new Set(['jpg', 'png']);
const normalizeFormat = (ext: string) => {
    const format = ext.split('.').pop()!.toLowerCase();
    return format === 'jpeg' ? 'jpg' : format;
};

const WEB_FORMATS = new Set(['webp', 'avif']);

// Adres wariantu w niekanonicznej postaci ma przekierować (301) na kanoniczną
// zamiast kończyć się błędem. Dwa przypadki:
//  - warianty w formacie źródła (jpg/png) powstają tylko w formacie
//    oryginału: po konwersji oryginału z png na jpg stary `.1200.png` ->
//    `.1200.jpg`;
//  - format zapisany inaczej niż kanonicznie (`.JPG`, `.jpeg`, `.AVIF` - np.
//    ze starych linków) -> ta sama nazwa małymi literami, jpeg jako jpg.
// Zwraca rozszerzenie docelowe albo null, gdy adres jest już kanoniczny.
// `requestedExtension` to sufiks z URL-a (np. ".1200.png"),
// `originalExtension` - rozszerzenie oryginału na dysku (np. ".jpg").
export function sourceFormatRedirect(requestedExtension: string, originalExtension: string): string | null {
    const raw = requestedExtension.split('.').pop()!;
    const requested = normalizeFormat(requestedExtension);
    const original = normalizeFormat(originalExtension);

    let target: string;
    if (SOURCE_FAMILY.has(requested) && SOURCE_FAMILY.has(original)) {
        target = original;
    } else if (WEB_FORMATS.has(requested)) {
        target = requested;
    } else {
        return null;
    }
    return raw === target ? null : target;
}

const VARIANT_WIDTHS = DEFAULT_WIDTHS.filter((w): w is number => w !== undefined).sort((a, b) => a - b);

// Sufiks wariantu: opcjonalna szerokość i format, np. ".1200.webp" albo ".png".
const VARIANT_EXTENSION = /^(?:\.(\d+))?\.([^.]+)$/;

// Adresy (PictureSrcHtml) zawierają tylko szerokości z DEFAULT_WIDTHS i tylko
// takie serwer generuje - inaczej każda liczba (.10001.webp, .10002.webp...)
// tworzyłaby w cache osobny plik. Inna szerokość wskazuje na największą
// standardową nie większą od niej (najmniejszą, gdy żądana jest mniejsza).
function canonicalWidth(width: number): number {
    return VARIANT_WIDTHS.filter((w) => w <= width).pop() ?? VARIANT_WIDTHS[0];
}

// Kanoniczny sufiks wariantu (szerokość + format) dla żądanego - różny od
// żądanego oznacza przekierowanie 301. null, gdy żądany sufiks w ogóle nie ma
// postaci wariantu (np. ".abc.webp", ".1.2.webp") - taki adres to 404.
// Parametry jak w sourceFormatRedirect.
export function canonicalVariantExtension(requestedExtension: string, originalExtension: string): string | null {
    const match = VARIANT_EXTENSION.exec(requestedExtension);
    if (!match) {
        return null;
    }
    const [, width, format] = match;
    const widthPart = width === undefined ? '' : `.${canonicalWidth(Number(width))}`;
    return `${widthPart}.${sourceFormatRedirect(requestedExtension, originalExtension) ?? format}`;
}
